import {
	ColumnsProps,
	ScrollType,
	buildColumn,
	getDefaultPaginationSettings,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	getPaginatedDataBy,
} from '@/Helper';
import { statusApi } from '@/api';
import { ButtonDropdown, EditableCell, ModalConfirmation } from '@/components/common';
import { BaseColumns, PaginationSetting, SelectOptions } from '@/model/CommonModel';
import { OptionsButtonBuilder } from '@/model/DeviceAdminModel';
import { Logger } from '@/model/LoggingModel';
import { DigiTracTransaction } from '@/model/StatusModel';
import { useStoreSelector } from '@/store';
import { selectTransactions } from '@/store/common/selectors';
import { CaretDownOutlined } from '@ant-design/icons';
import { Spin, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import React, { useEffect, useRef, useState } from 'react';
import styles from './queuedDownloadTable.module.scss';

type Props = {
	isExpanded: boolean;
};

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { y: 275 };
const columns: ColumnsProps<DigiTracTransaction>[] = [
	{
		...buildColumn(_('ID'), 'BatchId', '5%', 'left'),
		sorter: (a, b) => a.BatchId - b.BatchId,
	},
	{
		...buildColumn(_('Controller'), 'ControllerName', '20%', 'left'),
		sorter: (a, b) => a.ControllerName.localeCompare(b.ControllerName),
	},
	{
		...buildColumn(_('StartTime'), 'StartTime', '10%', 'left'),
		sorter: (a, b) => new Date(a.StartTime).getTime() - new Date(b.StartTime).getTime(),
	},
	{
		...buildColumn(_('Status'), 'Status', '10%', 'left'),
		sorter: (a, b) => (a.Status ? a.Status.localeCompare(b.Status) : ''),
	},
	{
		...buildColumn(_('Description'), 'Description', '20%', 'left'),
		sorter: (a, b) => a.Description.localeCompare(b.Description),
	},
	{
		...buildColumn(_('Count'), 'Count', '10%', 'left'),
		sorter: (a, b) => a.Count - b.Count,
	},
	{
		...buildColumn(_('Complete'), 'Complete', '10%', 'left'),
		sorter: (a, b) => a.Complete - b.Complete,
	},
	{
		...buildColumn(_('Progress'), 'Progress', '10%', 'left'),
		sorter: (a, b) => a.Progress - b.Progress,
		render: (value, row, index) => {
			return `${row.Progress}%`;
		},
	},
	{
		...buildColumn(_('Errors'), 'Errors', '10%', 'left'),
		sorter: (a, b) => a.Errors - b.Errors,
	},
];
const defaultPaginationSettings: PaginationSetting = getDefaultPaginationSettings();

const QueuedDownloadTable = ({ isExpanded }: Props) => {
	//#region State Management Variables
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [paginationSettings, setPaginationSettings] = useState<PaginationSetting>(defaultPaginationSettings);
	const [dataSource, setDataSource] = useState<DigiTracTransaction[]>([]);

	const suspendMode = useRef<boolean>(true);
	const pendingEvents = useRef<DigiTracTransaction[]>([]);
	const intervalRef = useRef<null | ReturnType<typeof setInterval>>(null);

	const transactions: DigiTracTransaction[] = useStoreSelector<DigiTracTransaction[]>(selectTransactions);
	//#endregion

	const items: OptionsButtonBuilder<string> = {
		labelOrIcon: '...',
		options: [
			{
				id: 'abortDownloadButton',
				label: _('AbortDownload'),
				value: 'abort',
			},
		],
	};
	const contextMenuOptions: SelectOptions<string>[] = items.options;

	useEffect(() => {
		statusApi
			.retrieveDownloadSummary()
			.then(response => {
				setDataSource(response);
				setIsLoading(false);
				suspendMode.current = false;
			})
			.catch(e => Logger.writeErrorLog(e));

		intervalRef.current = setInterval(() => {
			clearCompletedItems();
		}, 2 * 1000);
		return () => {
			if (intervalRef.current) {
				clearTimeout(intervalRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (transactions?.length) {
			if (suspendMode.current) {
				pendingEvents.current.push(...transactions);
			} else {
				addData(transactions);
			}
		}
	}, [transactions]);

	useEffect(() => {
		if (!suspendMode && pendingEvents?.current?.length) {
			addData(pendingEvents.current);
			pendingEvents.current = [];
		}
	}, [suspendMode]);

	const mappedColumns = columns.map(col => ({
		...col,
		onCell: (record: DigiTracTransaction) => ({
			record,
			dataIndex: col.dataIndex,
			title: col.title,
			options: contextMenuOptions,
			isTableInEditMode: false,
			onChangeSelection,
			onClickOption: handleActionScope,
		}),
	})) as ColumnsType<DigiTracTransaction>;

	const handleChangePagination = (page: number, pageSize: number): void => {
		if (pageSize !== paginationSettings.PageSize) {
			setPaginationSettings({ ...paginationSettings, PageNumber: page, PageSize: pageSize });
		} else {
			setPaginationSettings({ ...paginationSettings, PageNumber: page });
		}
	};

	const paginationTable: TablePaginationConfig = getDefaultTablePaginationConfig(
		false,
		handleChangePagination,
		paginationSettings.PageNumber,
		paginationSettings.PageSize,
		dataSource.length,
		undefined,
		dataSource.filter(x => x.checked).map<React.Key>(x => x.BatchId)
	);

	const addData = (newTransactions: DigiTracTransaction[]) => {
		setDataSource(prevCurrentDataSource => {
			const clonedState: DigiTracTransaction[] = [...prevCurrentDataSource];

			newTransactions.forEach(transaction => {
				let row = clonedState.find(x => x.BatchId === transaction.BatchId);
				if (row) {
					row.Status = transaction.Status;
					row.Progress = transaction.Progress;
					row.Complete = transaction.Complete;
					row.Errors = transaction.Errors;
					row.Count = transaction.Count;
					row.CompletedTime = transaction.CompletedTime;
				} else {
					row = transaction;
					clonedState.push(row);
				}

				if (row.Progress >= 100 && !row.CompletedTime) {
					row.CompletedTime = new Date().getTime();
				}
			});

			return clonedState;
		});
	};

	const onChangeSelection = (key: BaseColumns): void => {
		const batchId = key.key;
		let cloneStateProps: DigiTracTransaction[] = [];
		if (dataSource.findIndex(x => x.checked && x.BatchId === batchId) === -1) {
			dataSource.forEach(item => {
				item.checked = item.BatchId === batchId;
				cloneStateProps.push({ ...item });
			});
			setDataSource(cloneStateProps);
		}
	};

	const clearCompletedItems = () => {
		setDataSource(prevState => {
			if (!prevState?.length) {
				return prevState;
			}

			const currentTime = new Date().getTime();
			const result = prevState.filter(x => {
				if (!x.CompletedTime || x.Progress < 100) {
					return true;
				}

				const seconds = (currentTime - x.CompletedTime) / 1000;

				return seconds <= 5;
			});

			// Only update state if the data has actually changed
			return result.length !== prevState.length ? result : prevState;
		});
	};

	const handleOnClickAbortDownload = () => {
		const selectedRowKeys: number[] = dataSource.filter(x => x.checked).map<number>(x => x.BatchId);
		if (selectedRowKeys.length) {
			ModalConfirmation({
				title: _('DownloadCancelConfirmationAbort'),
				onConfirm: () => {
					statusApi.abortDownload(selectedRowKeys);
				},
				content: (
					<div>
						{_('CancelDownloadInstructions1')}
						<br />
						{_('CancelDownloadInstructions2')}
						<br />
						{_('AreYouSureYouWantToCancel')}
					</div>
				),
			});
		}
	};

	const handleActionScope = async (sender: string) => {
		const key: string = sender;
		switch (key) {
			case 'abort':
				handleOnClickAbortDownload();
				break;
		}
	};

	const handleChange = (newSelectedRowKeys: React.Key[]): void => {
		const cloneStateProps: DigiTracTransaction[] = [];
		dataSource.forEach(item => {
			item.checked = newSelectedRowKeys.findIndex(key => key.valueOf() === item.BatchId) !== -1;
			cloneStateProps.push({ ...item });
		});
		setDataSource(cloneStateProps);
	};

	const handleSelectAll = (): void => {
		setIsLoading(true);
		const cloneStateProps: DigiTracTransaction[] = [];
		dataSource.forEach(item => {
			cloneStateProps.push({ ...item, checked: true });
		});
		setDataSource(cloneStateProps);
		setIsLoading(false);
	};

	const handleSelectInvert = (): void => {
		setIsLoading(true);
		const currentPaginatedData: DigiTracTransaction[] = getPaginatedDataBy(dataSource, paginationSettings.PageNumber, paginationSettings.PageSize);
		const cloneState: DigiTracTransaction[] = dataSource.map(x => {
			let checked = x.checked;
			const item = currentPaginatedData.find(y => y.BatchId === x.BatchId);
			if (item) {
				checked = !item.checked;
			}

			return {
				...x,
				checked: checked,
			};
		});
		setDataSource(cloneState);
		setIsLoading(false);
	};

	const rowSelection: TableRowSelection<DigiTracTransaction> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(false, handleSelectAll, handleSelectInvert),
		getCheckboxProps: record => {
			return {
				id: `batch-${record['key']}`,
			};
		},
		selectedRowKeys: dataSource.filter(x => x.checked).map<React.Key>(x => x.BatchId),
		onChange: handleChange,
	};

	let content: React.ReactElement = <></>;
	if (!isLoading && isExpanded) {
		const tableData: DigiTracTransaction[] = dataSource.map(transaction => {
			return {
				...transaction,
				key: transaction.BatchId,
				Description: transaction.Description ? transaction.Description.replace(/\|/g, ' ') : '',
				StartTime: transaction.StartTime ? FormatDate(transaction.StartTime, true, null, true, false) : '',
			};
		});

		content = (
			<>
				<div className={styles.buttonContainer}>
					<ButtonDropdown
						id='enrollmentCredentialActionsDropdown'
						menuOptions={items.options}
						icon={<CaretDownOutlined />}
						labelIcon={items.labelOrIcon}
						onClickOption={handleActionScope}
						disabled={dataSource.filter(x => x.checked).length === 0}
					/>
				</div>
				<Table
					pagination={paginationTable}
					id='statusDashboardQueuedDownloadDownloadMonitorTable'
					columns={mappedColumns}
					dataSource={tableData}
					scroll={scroll}
					rowSelection={rowSelection}
					size='small'
					rowClassName={(record: DigiTracTransaction, index) => {
						if (index % 2 !== 0) {
							return styles.evenRow;
						}
					}}
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					className={styles.statusWidgetTable}
				/>
			</>
		);
	}

	return (
		<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='default' className={styles.spinContainer}>
			<div className={styles.queuedDownloadTableContainer}>{content}</div>
		</Spin>
	);
};

export { QueuedDownloadTable };
